import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { Add, HighlightOffRounded } from '@material-ui/icons';
import { makeStyles, Theme } from '@material-ui/core/styles';
import React, { useEffect, useRef, useState } from 'react';
import { CancelButton, SubmitButton } from '../custom-components/CustomButtons';
import { GreenCheck } from '../custom-components/CustomCheckboxes';
import { useDispatch, useSelector } from 'react-redux';
import { getProjectState } from '../../features/project/selectors';
import { FileNode, ICompany, IUser } from '../../api-client/autogenerated';
import { getGroupsState } from '../../features/groups/selector';
import {
  copyToClipboard,
  createQRCode,
  getFilteredUsersFromUserGroup,
  getPageTitle,
} from '../../scripts/utils';
import SelectCompanyUsersDialog from '../dialogs/SelectCompanyUsersDialog';
import Button from '@material-ui/core/Button';
import { getUserState } from '../../features/user/selectors';
import {
  getUsersAndUserGroupsFromFileNodes,
  ManagePermissionsDialogType,
} from './ManagePermissionsDialog';
import { notifyUsersOfAccessToDesignFile } from '../../models/api/filesystem';
import DistributionList from '../form-pages/user-groups/DistributionList';
import _ from 'lodash';
import CircularLoader from '../loader/CircularLoader';
import validator from 'validator';
import DividerWithText from '../custom-components/DividerWithText';
import { addSnackbar } from '../../features/snackbar/actions';
import { getDocumentState } from '../../features/document/selectors';
import { getDocumentsType } from '../../features/documents/selectors';
import { isMobile } from 'react-device-detect';

const isEmail = validator.isEmail;

interface EmailUsersDialogProps {
  dialogOpen: boolean;
  files?: FileNode[];
  closeDialog: () => void;
  isDesignNotify?: boolean;
  onSubmit?: (emails: string[], note?: string) => Promise<void>;
  defaultSubject?: string;
  defaultDescription?: string;
  handleCopy?: () => void;
  disableSnackbar?: boolean;
  getLink?: () => Promise<string>;
  /**
   * Whether to fetch the link as soon as the dialog opens. False where getLink has a side
   * effect worth deferring - a folder link is backed by a share row, so fetching it eagerly
   * would share the folder before the user has asked for anything.
   */
  generateLinkOnOpen?: boolean;
  /**
   * CENTERLINE users who can be picked as recipients, offered through Add Recipients alongside the
   * manual email field. Passed in rather than read from the loaded project so this works where no
   * project is loaded -- project exports, for one, only exist on archived projects, which never do.
   */
  selectableCompanies?: ICompany[];
  /** The project to name on the email, for the same reason. Falls back to the loaded project. */
  projectName?: string;
  /**
   * A subject to start from that the user can still edit, unlike defaultSubject, which fixes it.
   * Used where one dialog sends both kinds of email and neither wording is the only right one.
   */
  initialSubject?: string;
  /** Overrides the heading, for a dialog that is neither purely a notification nor purely a link. */
  title?: string;
  /**
   * Hides the Copy Link and Download QR Code options, for a dialog whose email is the only way to
   * hand out what it is sharing.
   */
  hideLinkOptions?: boolean;
  /**
   * The body the recipients will read, shown read-only so the sender can see what is going out.
   * The dialog does not compose it -- it is rendered from a template server-side.
   */
  bodyPreview?: string;
}

const useStyles = makeStyles({
  titleContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '8px 16px 8px 16px',
    borderBottom: '1px solid #EDECEC',
  },
  title: {
    marginTop: '3px',
    marginLeft: '6px',
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '26px',
    lineHeight: '30px',
    textAlign: 'left',
    color: '#0947B9',
    textTransform: 'none',
  },
  subtitle: {
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 'bold',
    fontSize: '18px',
    lineHeight: '21px',
    textAlign: 'left',
    textTransform: 'none',
    color: '#464546',
    maxWidth: '600px',
  },
  rootIconButton: {
    padding: 0,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  label: {
    color: '#0947B9',
    fontWeight: 500,
    fontSize: '1.125rem',
    marginRight: 15,
    marginBottom: 25,
  },
  companyText: {
    fontSize: '20px',
    lineHeight: '32px',
    textTransform: 'none',
    color: 'gray',
    fontWeight: 500,
  },
  userText: {
    fontSize: '14px',
    lineHeight: '16px',
    textTransform: 'none',
    color: 'gray',
  },
  submit: {
    marginTop: 16,
    marginBottom: 24,
    fontSize: '1em',
  },
});

export default function EmailUsersDialog(props: EmailUsersDialogProps) {
  const classes = useStyles();
  const {
    dialogOpen,
    closeDialog,
    files,
    isDesignNotify,
    onSubmit,
    defaultSubject,
    defaultDescription,
    disableSnackbar,
    getLink,
    generateLinkOnOpen = true,
    selectableCompanies,
    projectName,
    initialSubject,
    title,
    hideLinkOptions,
    bodyPreview,
  } = props;

  const dispatch = useDispatch();
  const user = useSelector(getUserState);
  const currentDocument = useSelector(getDocumentState);
  const docType = useSelector(getDocumentsType);
  const project = useSelector(getProjectState);
  const groups = useSelector(getGroupsState);

  const isXs = useMediaQuery<Theme>((theme) => theme.breakpoints.down('xs'));

  const qrcodeRef = useRef<HTMLDivElement>(null);

  const [inputSubject, setInputSubject] = useState<string>();
  const [inputDescription, setInputDescription] = useState<string>();

  const [selectRecipientsDialogOpen, setSelectRecipientsDialogOpen] = useState(false);

  const [eligibleUserIds, setEligibleUserIds] = useState<string[]>([]);
  const [eligibleUserGroupIds, setEligibleUserGroupIds] = useState<string[]>([]);

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);

  const [emails, setEmails] = useState<string[]>([]);
  const [inputEmail, setInputEmail] = useState('');
  const [error, setError] = useState('');

  const [link, setLink] = useState('');
  const [qrcodeCreated, setQrcodeCreated] = useState(false);
  const [qrcodeLinkElement, setQrcodeLinkElement] = useState<HTMLAnchorElement>();

  const [willSendToSelf, setWillSendToSelf] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (files) {
      const { userIds, userGroupIds } = getUsersAndUserGroupsFromFileNodes(files);
      setEligibleUserIds(userIds);
      setEligibleUserGroupIds(userGroupIds);
    }
  }, [files]);

  useEffect(() => {
    if (defaultSubject) setInputSubject(defaultSubject);
  }, [defaultSubject]);

  // Only a starting point: unlike defaultSubject it leaves the field editable, and it is not
  // reapplied once the dialog is open, so it never overwrites what the user has typed.
  useEffect(() => {
    if (dialogOpen && initialSubject) setInputSubject((previous) => previous || initialSubject);
  }, [dialogOpen, initialSubject]);

  useEffect(() => {
    if (defaultDescription) setInputDescription(defaultDescription);
  }, [defaultDescription]);

  const setUserAndGroupIds = (userIds: string[], groupIds: string[]) => {
    setSelectedUserIds(userIds);
    setSelectedGroupIds(groupIds);
  };

  const getUsers = () => {
    let users = [] as IUser[];
    selectedUserIds.forEach((userId) => {
      const projectUser = project?.projectUserList?.find((pUser) => pUser.userId === userId);
      if (projectUser?.user) users.push(projectUser.user);
    });
    selectedGroupIds.forEach((groupId) => {
      const group = groups.find((group) => group.id === groupId);
      if (group) {
        users.push(...getFilteredUsersFromUserGroup(ManagePermissionsDialogType.Design, group));
      }
    });
    if (willSendToSelf) users.push(user);
    return _.uniqBy(users, (user) => user.id);
  };

  const getEmails = () => {
    // Recipients come out of the picker as ids, but this dialog sends to addresses.
    const selectedRecipientEmails = _.compact(
      (selectableCompanies ?? [])
        .flatMap((company) => company.users ?? [])
        .filter(({ id }) => selectedUserIds.includes(id))
        .map(({ email }) => email),
    );
    const allEmails = [...selectedRecipientEmails, ...emails];
    // Where the dialog also sends a notification, the copy to self rides on that instead: the
    // sender is a CENTERLINE user, so mailing them a public link as well is one email too many.
    if (willSendToSelf && !isDesignNotify) return _.uniq([user.email, ...allEmails]);
    return _.uniq(allEmails);
  };

  /**
   * Adds whatever the field holds: one address, or a list pasted out of somebody's mail client,
   * which is where the commas and semicolons come from. Everything valid is taken and the field
   * keeps only what was not, so a single typo does not cost the rest of the list.
   */
  const handleAddEmail = (email: string, event?: React.KeyboardEvent<HTMLDivElement>) => {
    if (event && event.key !== 'Enter') return;
    const candidates = inputEmail
      .split(/[,;]/)
      .map((value) => value.trim())
      .filter(Boolean);
    if (candidates.length === 0) return;

    const rejected = candidates.filter((value) => !isEmail(value));
    const accepted = candidates.filter((value) => isEmail(value) && !emails.includes(value));
    const duplicates = candidates.filter((value) => isEmail(value) && emails.includes(value));

    if (accepted.length > 0) setEmails((prev) => _.uniq([...prev, ...accepted]));
    setInputEmail(rejected.join(', '));

    if (rejected.length > 0) {
      setError(rejected.length === 1 ? 'Invalid email' : `${rejected.length} invalid emails`);
    } else if (accepted.length === 0 && duplicates.length > 0) {
      setError(duplicates.length === 1 ? 'Email already exists' : 'Emails already exist');
    } else {
      setError('');
    }
  };

  const handleSubmit = async () => {
    // A CENTERLINE user gets a real notification against the file; anyone else gets a link to it.
    // A dialog offering both sends both, one for each kind of recipient it was actually given.
    const linkEmails = getEmails();
    const hasNotifiableRecipients =
      selectedUserIds.length > 0 || selectedGroupIds.length > 0 || willSendToSelf;
    closeDialog();
    if (isDesignNotify && hasNotifiableRecipients) {
      await handleNotifyUsers();
    }
    if (onSubmit && linkEmails.length > 0) {
      await onSubmit(linkEmails, inputDescription);
    }
    if (!disableSnackbar) {
      dispatch(
        addSnackbar({
          id: Date.now(),
          message: 'Successfully sent email(s)!',
          severity: 'success',
        }),
      );
    }
  };

  const handleNotifyUsers = async () => {
    if (!project || !inputSubject || !files) return;
    try {
      setIsSubmitting(true);
      await notifyUsersOfAccessToDesignFile(
        project.id,
        files.map((f) => ({ ownerId: f.ownerId, projectId: f.projectId, fullKey: f.fullKey })),
        inputSubject,
        willSendToSelf ? [...selectedUserIds, user.id] : selectedUserIds,
        selectedGroupIds,
        inputDescription,
      );
      setIsSubmitting(false);
      closeDialog();
    } catch (e: any) {
      console.error(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (dialogOpen && getLink && generateLinkOnOpen) {
      getLink().then((link) => setLink(link));
    }

    if (!dialogOpen) {
      setLink('');
      setQrcodeCreated(false);
    }
  }, [dialogOpen]);

  useEffect(() => {
    if (link && qrcodeRef.current && !qrcodeCreated) {
      const hasSubtitle = !!(docType && currentDocument);
      createQRCode({
        qrcodeRef: qrcodeRef.current,
        link,
        quietZone: 16,
        title: projectName ?? project?.name,
        subTitle: hasSubtitle ? getPageTitle(docType!, currentDocument) : undefined,
        onRenderingEnd: (options, url) => {
          const link = document.createElement('a');
          link.href = url;
          link.download = 'qrcode.png';
          setQrcodeLinkElement(link);
        },
        drawer: 'canvas',
      });
      setQrcodeCreated(true);
    }
  }, [link]);

  const handleCopy = async () => {
    // Deferred link generation: fetch it now, on the click that actually asks for a link.
    let linkToCopy = link;
    if (!linkToCopy && getLink) {
      linkToCopy = await getLink();
      setLink(linkToCopy);
    }
    if (linkToCopy) {
      try {
        await copyToClipboard(linkToCopy);
        dispatch(
          addSnackbar({
            id: Date.now(),
            message: 'Link copied to clipboard',
            severity: 'success',
          }),
        );
      } catch {
        dispatch(
          addSnackbar({
            id: Date.now(),
            message: 'Error copying link',
            severity: 'error',
          }),
        );
      }
    } else {
      dispatch(
        addSnackbar({
          id: Date.now(),
          message: 'Error retrieving link',
          severity: 'error',
        }),
      );
    }
  };

  const handleDownloadQrcode = () => {
    if (qrcodeLinkElement) {
      qrcodeLinkElement.click();
    }
  };

  const getTitle = () => {
    if (title) return title;
    if (isDesignNotify) return 'Create Notification Email';
    if (onSubmit) return 'Send A Link';
    return 'Get A Link';
  };

  const copyLinkContent = (
    <div style={{ padding: '0px 24px' }}>
      <Button
        onClick={handleCopy}
        fullWidth
        variant="contained"
        color="primary"
        className={classes.submit}
        disabled={!link && !getLink}
      >
        Copy Link
      </Button>
      <DividerWithText>OR</DividerWithText>
      <Button
        fullWidth
        variant="contained"
        color="primary"
        disabled={!qrcodeLinkElement}
        onClick={handleDownloadQrcode}
        className={classes.submit}
      >
        Download QR Code
      </Button>
      <div ref={qrcodeRef} style={{ display: 'none' }} />
    </div>
  );

  /** Picking CENTERLINE users, for the half of the dialog that notifies them. */
  const showRecipientPicker = isDesignNotify || !!selectableCompanies;
  /** Typing addresses, for the half that sends a link to people who are not CENTERLINE users. */
  const showEmailField = !isDesignNotify || !!onSubmit;

  const getContent = () => {
    if (isDesignNotify || onSubmit) {
      return (
        <>
          <DialogContent style={{ paddingBottom: 16, paddingTop: 0 }}>
            <div style={{ display: 'flex', flexDirection: 'column', margin: '16px 0px 8px 0px' }}>
              {showRecipientPicker && (
                <Button
                  color="primary"
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setSelectRecipientsDialogOpen(true)}
                  // A phone has no room for a button a third of the row wide that will not wrap.
                  style={{
                    width: isXs ? '100%' : '35%',
                    whiteSpace: 'nowrap',
                    marginBottom: showEmailField ? 16 : 0,
                  }}
                >
                  Add Recipients
                </Button>
              )}
              {showEmailField && (
                <>
                  <Typography style={{ marginBottom: '12px' }}>
                    {showRecipientPicker ? 'Or input' : 'Input'} valid email addresses, separated by
                    commas or semicolons, then press the <strong>Enter</strong> key or click{' '}
                    <strong>Add</strong>.
                  </Typography>
                  <Typography style={{ color: 'red', marginBottom: '6px' }}>{error}</Typography>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      label="Email Address"
                      value={inputEmail}
                      onChange={(e) => {
                        setError('');
                        setInputEmail(e.target.value);
                      }}
                      onKeyDown={(e) => handleAddEmail(inputEmail, e)}
                      style={{ flexShrink: 2 }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<Add />}
                      onClick={() => handleAddEmail(inputEmail)}
                      style={{ marginLeft: 8, padding: '0px 12px' }}
                    >
                      Add
                    </Button>
                  </div>
                </>
              )}
              <FormControlLabel
                control={
                  <GreenCheck
                    value={willSendToSelf}
                    onChange={(e, checked) => setWillSendToSelf(checked)}
                  />
                }
                label={<Typography>Send a copy of this notification to me</Typography>}
                style={{ marginTop: 8 }}
              />
            </div>
            <Divider variant="fullWidth" style={{ marginBottom: 16 }} />
            <div
              style={{
                marginBottom: 16,
                paddingTop: 0,
                width: '100%',
                overflowY: 'auto',
                maxHeight: '400px',
                display: 'flex',
                alignItems: 'flex-start',
              }}
            >
              <Typography className={classes.label} style={{ marginBottom: 0, marginTop: 6 }}>
                To:
              </Typography>
              {selectedGroupIds.length === 0 &&
              selectedUserIds.length === 0 &&
              !willSendToSelf &&
              getEmails().length === 0 ? (
                <Typography variant="body2" style={{ marginBottom: 0, marginTop: 6 }}>
                  {isDesignNotify
                    ? 'There are no users currently selected to be notified'
                    : 'No recipients'}
                </Typography>
              ) : (
                // Both kinds of recipient at once where the dialog offers both: the CENTERLINE
                // users who will be notified, and the addresses that will be sent a link.
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  {isDesignNotify && getUsers().length > 0 ? (
                    <DistributionList open limitHeight users={getUsers()} />
                  ) : null}
                  {getEmails().length > 0 ? (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        paddingLeft: 32,
                      }}
                    >
                      {getEmails().map((email, index) => (
                        <Typography
                          key={email + index}
                          style={{ marginBottom: 2, overflowWrap: 'anywhere' }}
                        >
                          {email}
                        </Typography>
                      ))}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
            <div>
              <Divider variant="fullWidth" style={{ marginBottom: 16 }} />
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 25 }}>
                <Typography className={classes.label} style={{ marginBottom: 0 }}>
                  From:
                </Typography>
                <div style={{ display: 'block', marginLeft: 8 }}>
                  <Typography className={classes.companyText}>{user.company?.name}</Typography>
                  <Typography className={classes.userText}>{user.name}</Typography>
                </div>
              </div>
              <div style={{ display: 'flex' }}>
                <Typography className={classes.label}>Project Name:</Typography>
                <Typography>{projectName ?? project?.name}</Typography>
              </div>
              {isDesignNotify && (
                <div style={{ display: 'flex' }}>
                  <Typography className={classes.label}>File/Folder:</Typography>
                  <div style={{ marginBottom: 25 }}>
                    {files?.map((file) => (
                      <Typography key={file.fullKey} style={{ fontWeight: 500, marginBottom: 4 }}>
                        {file.relativeKey}
                      </Typography>
                    ))}
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 25 }}>
                <Typography className={classes.label} style={{ marginBottom: 0 }}>
                  Subject:
                </Typography>
                <TextField
                  label="Subject (max 100 characters)"
                  variant="outlined"
                  size="small"
                  fullWidth
                  disabled={!!defaultSubject}
                  value={inputSubject}
                  onChange={(e) => setInputSubject(e.target.value)}
                  inputProps={{ maxLength: 100 }}
                />
              </div>
              {bodyPreview && (
                <div style={{ display: 'flex', marginBottom: 25 }}>
                  <Typography className={classes.label} style={{ marginBottom: 0 }}>
                    Message:
                  </Typography>
                  <TextField
                    variant="outlined"
                    fullWidth
                    multiline
                    disabled
                    rows={14}
                    value={bodyPreview}
                    InputProps={{ style: { padding: 10 } }}
                  />
                </div>
              )}
              {isDesignNotify && (
                <div style={{ display: 'flex' }}>
                  <Typography className={classes.label} style={{ marginBottom: 0 }}>
                    Note:
                  </Typography>
                  <TextField
                    label="Note (optional)"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={4}
                    value={inputDescription}
                    onChange={(e) => setInputDescription(e.target.value)}
                    InputProps={{ style: { padding: 10 } }}
                  />
                </div>
              )}
            </div>
          </DialogContent>
          <DialogActions
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: 24,
              paddingBottom: 8,
            }}
          >
            <CancelButton onClick={closeDialog} />
            {!isSubmitting ? (
              <SubmitButton
                disabled={
                  !inputSubject ||
                  (selectedGroupIds.length === 0 &&
                    selectedUserIds.length === 0 &&
                    getEmails().length === 0)
                }
                onClick={handleSubmit}
                style={{ padding: '0px 16px' }}
              >
                Send Emails
              </SubmitButton>
            ) : (
              <CircularLoader size={30} />
            )}
          </DialogActions>
          {!hideLinkOptions && (
            <>
              <DividerWithText style={{ paddingLeft: 24, paddingRight: 24 }}>OR</DividerWithText>
              {copyLinkContent}
            </>
          )}
          <SelectCompanyUsersDialog
            open={selectRecipientsDialogOpen}
            handleClose={() => setSelectRecipientsDialogOpen(false)}
            existingUserIds={[]}
            defaultSelectedUserIds={selectedUserIds}
            eligibleUserIds={selectableCompanies ? undefined : eligibleUserIds}
            // No user groups to offer when the recipients were handed to us, and no design
            // permission to read either -- whoever supplied them has already said who is eligible.
            eligibleUserGroupIds={selectableCompanies ? undefined : eligibleUserGroupIds}
            eligibleCompaniesOverride={selectableCompanies}
            submit={setUserAndGroupIds}
            type={
              selectableCompanies
                ? ManagePermissionsDialogType.None
                : ManagePermissionsDialogType.Design
            }
          />
        </>
      );
    }

    return copyLinkContent;
  };

  return (
    <Dialog
      open={dialogOpen}
      onClose={closeDialog}
      onContextMenu={(e) => e.stopPropagation()}
      maxWidth="md"
      fullScreen={isXs && !!(onSubmit || isDesignNotify)}
      PaperProps={
        isXs ? undefined : { style: { minWidth: onSubmit || isDesignNotify ? 600 : 300 } }
      }
      onDoubleClick={(e) => e.stopPropagation()}
    >
      <DialogTitle className={classes.titleContainer}>
        <Typography className={classes.title} align="left">
          {getTitle()}
        </Typography>
        <IconButton
          style={{ right: '20px', top: '12px', position: 'absolute' }}
          onClick={closeDialog}
          classes={{
            root: classes.rootIconButton,
          }}
        >
          <HighlightOffRounded />
        </IconButton>
      </DialogTitle>
      {getContent()}
    </Dialog>
  );
}
